import type { SimulationTableColumnMeta } from 'types/ppaSimulationMeta';
import type { PPAScenarioColumnVisibilityInput } from 'types/ppaViews';
import type {
    SimulationKpiKind,
    SimulationTableApiRow,
    SimulationTableGroupByItem,
    SimulationTableKpiItem,
    SimulationVisibleAttributeColumn,
    SimulationVisibleKpiColumn,
} from './simulationTable.helpersTypes';

const IMPLEMENTATION_KPI_IDS = new Set(['implementation_date', 'implement_from']);
const COMPETITOR_KPI_IDS = new Set(['competitor_to_follow', 'competitor_follows']);
const DATE_KPI_TYPES = new Set(['time_effective', 'date', 'date_picker']);
const CHECKBOX_KPI_TYPES = new Set(['boolean', 'checkbox', 'flag']);
const INPUT_KPI_TYPES = new Set(['input', 'output', 'user_input']);
const DISPLAY_KPI_TYPES = new Set(['display_only', 'display', 'dependency']);

export function columnType(column: SimulationTableColumnMeta): string {
    return column.type?.trim().toLowerCase() ?? '';
}

export function normalizeSimulationAttributeKey(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
}

export function collectHierarchyAttributeTypes(rows: SimulationTableApiRow[]): string[] {
    const ordered: string[] = [];
    const seen = new Set<string>();

    rows.forEach((row) => {
        (row.hierarchy ?? []).forEach((item) => {
            const attributeType = item.attribute_type?.trim();
            if (!attributeType) return;
            const key = normalizeSimulationAttributeKey(attributeType);
            if (!key || seen.has(key)) return;
            seen.add(key);
            ordered.push(attributeType);
        });
    });

    return ordered;
}

export function resolveVisibleAttributeColumns({
    columnVisibility,
    groupBy,
    activeLensId,
    hierarchyAttributeTypes,
}: {
    columnVisibility?: PPAScenarioColumnVisibilityInput[];
    groupBy?: SimulationTableGroupByItem[];
    activeLensId?: number | null;
    hierarchyAttributeTypes: string[];
}): SimulationVisibleAttributeColumn[] {
    const groupByLabelByKey = new Map<string, string>();
    (groupBy ?? []).forEach((item) => {
        const key = normalizeSimulationAttributeKey(item.attribute_key ?? '');
        const label = item.display_name?.trim() || item.attribute_key?.trim();
        if (key && label) groupByLabelByKey.set(key, label);
    });

    const visibilityKeyByNormalized = new Map<string, string>();
    (columnVisibility ?? [])
        .filter((column) => {
            if (column.item_type?.toUpperCase() === 'SYSTEM_COLUMN') return false;
            if (activeLensId == null) return true;
            return column.lens_id === activeLensId;
        })
        .forEach((column) => {
            const key = column.attribute_key?.trim();
            if (!key) return;
            const normalized = normalizeSimulationAttributeKey(key);
            if (normalized && !visibilityKeyByNormalized.has(normalized)) {
                visibilityKeyByNormalized.set(normalized, key);
            }
        });

    const seenTypes = new Set<string>();
    return hierarchyAttributeTypes
        .map((attributeType) => attributeType.trim())
        .filter((attributeType) => {
            const normalized = normalizeSimulationAttributeKey(attributeType);
            if (!normalized || seenTypes.has(normalized)) return false;
            seenTypes.add(normalized);
            return true;
        })
        .map((attributeType) => {
            const normalized = normalizeSimulationAttributeKey(attributeType);
            const attributeKey = visibilityKeyByNormalized.get(normalized) || attributeType;
            return {
                attributeKey,
                displayName: groupByLabelByKey.get(normalized) || attributeType,
            };
        });
}

export function resolveSimulationKpiKind(kpi: SimulationTableKpiItem): SimulationKpiKind {
    const type = (kpi.kpi_type ?? kpi.kind ?? '').trim().toLowerCase();
    const id = (kpi.kpi_id ?? '').trim().toLowerCase();

    if (DATE_KPI_TYPES.has(type) || IMPLEMENTATION_KPI_IDS.has(id)) return 'date';
    if (CHECKBOX_KPI_TYPES.has(type) || COMPETITOR_KPI_IDS.has(id) || typeof kpi.value === 'boolean') {
        return 'checkbox';
    }
    if (INPUT_KPI_TYPES.has(type) || kpi.is_editable === true) return 'input';
    if (DISPLAY_KPI_TYPES.has(type)) return 'display';
    if (kpi.change != null || kpi.updated_value != null) return 'input';
    return 'display';
}

function kpiDisplayName(kpi: SimulationTableKpiItem): string {
    return kpi.display_label?.trim() || kpi.kpi_name?.trim() || '';
}

export function collectVisibleKpiColumns(
    rows: SimulationTableApiRow[],
): Record<SimulationKpiKind, SimulationVisibleKpiColumn[]> {
    const grouped: Record<SimulationKpiKind, SimulationVisibleKpiColumn[]> = {
        display: [],
        input: [],
        date: [],
        checkbox: [],
    };
    const seen = new Set<string>();

    rows.forEach((row) => {
        (row.kpi ?? []).forEach((kpi) => {
            const kpiId = kpi.kpi_id?.trim();
            if (!kpiId) return;
            const kind = resolveSimulationKpiKind(kpi);
            const seenKey = `${kind}:${normalizeSimulationAttributeKey(kpiId)}`;
            if (seen.has(seenKey)) return;
            seen.add(seenKey);
            grouped[kind].push({
                kpiId,
                kind,
                displayName: kpiDisplayName(kpi),
                valuePrefix: typeof kpi.valuePrefix === 'string' ? kpi.valuePrefix : undefined,
            });
        });
    });

    const usedAsInput = new Set(grouped.input.map((kpi) => normalizeSimulationAttributeKey(kpi.kpiId)));
    grouped.display = grouped.display.filter((kpi) => !usedAsInput.has(normalizeSimulationAttributeKey(kpi.kpiId)));

    return grouped;
}

export function findSimulationTableColumn(
    columns: SimulationTableColumnMeta[],
    matcher: (column: SimulationTableColumnMeta) => boolean,
): SimulationTableColumnMeta | undefined {
    return columns.find(matcher);
}
